import type {
  BatchComponentBalanceDto,
  BatchConsumptionDto,
  BatchDto,
  BatchStatus,
  ItemDto,
} from '@gsls/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import {
  Badge,
  Button,
  Card,
  CardBody,
  ErrorNote,
  Field,
  InfoNote,
  PageHeader,
  Spinner,
  Tooltip,
  inputClass,
} from '../components/ui';
import { DataTable, type DataColumn } from '../components/DataTable';
import { CodeField } from '../components/CodeField';
import { FilterBar } from '../components/FilterBar';
import { StickyActionBar } from '../components/StickyActionBar';
import { ApiError, api } from '../lib/api';
import { formatDateTime, formatQuantity, formatUnitCost } from '../lib/format';
import { useDeepLink } from '../lib/useDeepLink';
import { Can } from '../lib/session';
import { TracePanel } from '../components/TracePanel';

const STATUS_TONES: Record<BatchStatus, 'green' | 'accent' | 'hazard' | 'muted'> = {
  draft: 'muted',
  in_progress: 'accent',
  completed: 'green',
  closed: 'green',
  cancelled: 'muted',
};

const STATUS_LABELS: Record<BatchStatus, string> = {
  draft: 'Draft',
  in_progress: 'In progress',
  completed: 'Completed',
  closed: 'Closed',
  cancelled: 'Cancelled',
};

/**
 * Production batches.
 *
 * Consumption posts to the inventory ledger line by line as it is recorded, so
 * the quantity on hand is never wrong while a batch is running.
 */
export function BatchesPage() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);

  /** The dashboard opens a specific batch, or the new-batch form, directly. */
  useDeepLink('batch', (value) => {
    setSelectedId(value);
    setFormOpen(false);
  });
  useDeepLink('action', (value) => {
    if (value === 'new') {
      setFormOpen(true);
      setSelectedId(null);
    }
  });

  const batches = useQuery({
    queryKey: ['batches', { search, statusFilter }],
    queryFn: () =>
      api.batches.list({
        search,
        ...(statusFilter ? { status: statusFilter } : {}),
        limit: 100,
      }),
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['batches'] });
    void queryClient.invalidateQueries({ queryKey: ['inventory'] });
  };

  const selected = batches.data?.items.find((batch) => batch.id === selectedId) ?? null;

  const batchColumns: DataColumn<BatchDto>[] = [
    { key: 'code', header: 'Batch', cell: (batch) => <span className="font-mono text-xs">{batch.code}</span> },
    {
      key: 'product',
      header: 'Product',
      primary: true,
      cell: (batch) => (
        <>
          <span className="font-mono text-xs">{batch.itemCode}</span>
          <br />
          <span className="text-ink-body">{batch.itemName}</span>
        </>
      ),
    },
    {
      key: 'recipe',
      header: 'Recipe',
      cell: (batch) => (
        <span className="text-xs">
          {batch.recipeCode} v{batch.recipeVersion}
        </span>
      ),
    },
    {
      key: 'target',
      header: 'Target',
      cell: (batch) => (
        <span className="whitespace-nowrap">{formatQuantity(batch.targetQuantity, batch.uom)}</span>
      ),
    },
    {
      key: 'consumed',
      header: 'Consumed',
      cell: (batch) => <span className="whitespace-nowrap">{formatQuantity(batch.consumedValue)}</span>,
    },
    {
      key: 'produced',
      header: 'Produced',
      align: 'right',
      cell: (batch) => (
        <span className="whitespace-nowrap">
          {batch.outputQuantity
            ? `${formatQuantity(batch.outputQuantity, batch.uom)}${
                batch.yieldPercent ? ` (${batch.yieldPercent}%)` : ''
              }`
            : '—'}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      cell: (batch) => (
        <Badge tone={STATUS_TONES[batch.status]}>{STATUS_LABELS[batch.status]}</Badge>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        eyebrow="Production"
        title="Batches"
        description="A batch pins one approved recipe version and draws its material out of inventory as it goes. What it produces lands back in inventory carrying the cost of everything it used."
        actions={
          <Can capability="batch.manage">
            <Button
              onClick={() => {
                setFormOpen(true);
                setSelectedId(null);
              }}
            >
              New batch
            </Button>
          </Can>
        }
      />

      {formOpen ? (
        <BatchForm
          onClose={() => setFormOpen(false)}
          onSaved={(created) => {
            invalidate();
            setFormOpen(false);
            setSelectedId(created.id);
          }}
        />
      ) : null}

      {selected ? (
        <BatchDetail
          batch={selected}
          onChanged={() => {
            invalidate();
            void queryClient.invalidateQueries({ queryKey: ['batches', 'detail', selected.id] });
          }}
          onClose={() => setSelectedId(null)}
        />
      ) : null}

      <Card>
        <CardBody>
          <FilterBar activeCount={(search ? 1 : 0) + (statusFilter ? 1 : 0)}>
            <div className="mb-4 flex flex-wrap items-center gap-3">
              <input
                name="batchSearch"
                className={`${inputClass} max-w-xs`}
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search batch, product or recipe"
                aria-label="Search batches"
              />
              <select
                name="batchStatus"
                className={`${inputClass} max-w-[12rem]`}
                value={statusFilter}
                onChange={(event) => setStatusFilter(event.target.value)}
                aria-label="Filter by status"
              >
                <option value="">Any status</option>
                {Object.entries(STATUS_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
              {batches.isSuccess ? (
                <span className="text-sm text-ink-muted">
                  {batches.data.total} batch{batches.data.total === 1 ? '' : 'es'}
                </span>
              ) : null}
            </div>
          </FilterBar>

          {batches.isPending ? <Spinner /> : null}
          <ErrorNote>
            {batches.isError ? 'Could not load batches. Is the API running?' : ''}
          </ErrorNote>

          {batches.isSuccess ? (
            <DataTable
              rows={batches.data.items}
              columns={batchColumns}
              rowKey={(batch) => batch.id}
              rowClassName={() => 'hover:bg-brand-soft/40'}
              actions={(batch) => (
                <Button tone="ghost" onClick={() => setSelectedId(batch.id)}>
                  Open
                </Button>
              )}
              empty="No batches yet. A batch is one production run of an approved recipe."
            />
          ) : null}
        </CardBody>
      </Card>

      <StickyActionBar
        actions={[{ label: 'New batch', onClick: () => setFormOpen(true), capability: 'batch.manage', tone: 'primary' }]}
        hidden={formOpen || Boolean(selected)}
      />
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* Create                                                                      */
/* -------------------------------------------------------------------------- */

function BatchForm({
  onClose,
  onSaved,
}: {
  onClose: () => void;
  onSaved: (created: BatchDto) => void;
}) {
  const [recipeId, setRecipeId] = useState('');
  const [targetQuantity, setTargetQuantity] = useState('');
  const [locationId, setLocationId] = useState('');
  const [code, setCode] = useState('');
  const [scheduledFor, setScheduledFor] = useState('');
  const [notes, setNotes] = useState('');

  const recipes = useQuery({
    queryKey: ['recipes', { status: 'approved' }],
    queryFn: () => api.recipes.list({ status: 'approved', limit: 100 }),
  });
  const locations = useQuery({
    queryKey: ['locations', { limit: 200 }],
    queryFn: () => api.locations.list({ limit: 200 }),
  });

  const selectedRecipe = recipes.data?.items.find((recipe) => recipe.id === recipeId);
  const productItemId = selectedRecipe?.itemId;

  const create = useMutation({
    mutationFn: () =>
      api.batches.create({
        recipeId,
        targetQuantity: targetQuantity.trim(),
        locationId,
        ...(code.trim() ? { code: code.trim() } : {}),
        ...(scheduledFor
          ? { scheduledFor: new Date(`${scheduledFor}T00:00:00Z`).toISOString() }
          : {}),
        ...(notes.trim() ? { notes: notes.trim() } : {}),
      }),
    onSuccess: onSaved,
  });

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    create.mutate();
  }

  return (
    <Card className="mb-6">
      <CardBody>
        <h2 className="text-base font-bold text-ink">New batch</h2>
        <p className="mt-1 text-sm font-light text-ink-body">
          Only approved recipes can be run, and the version is pinned to the batch for good.
        </p>

        <form
          autoComplete="off"
          onSubmit={handleSubmit}
          className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3"
        >
          <Field
            label="Recipe"
            hint="Approved versions only"
            tooltip="The recipe this batch is pinned to. Pinning means the batch will always report the version it was actually made from, even after the recipe is revised. Only an approved version can be run."
          >
            <select
              name="batchRecipe"
              className={inputClass}
              value={recipeId}
              onChange={(event) => {
                setRecipeId(event.target.value);
                const recipe = recipes.data?.items.find((item) => item.id === event.target.value);
                if (recipe) setTargetQuantity(recipe.outputQuantity);
              }}
              required
            >
              <option value="">Select a recipe…</option>
              {recipes.data?.items.map((recipe) => (
                <option key={recipe.id} value={recipe.id}>
                  {recipe.code} v{recipe.version} — {recipe.itemCode} ({recipe.outputQuantity}{' '}
                  {recipe.outputUom})
                </option>
              ))}
            </select>
          </Field>

          <Field
            label="Batch size"
            hint={selectedRecipe ? `Recipe is stated for ${selectedRecipe.outputQuantity} ${selectedRecipe.outputUom}` : undefined}
            tooltip="How much this run is meant to produce. Every component quantity on the recipe is scaled to this size, and how much actually comes out is recorded separately when the batch is completed."
          >
            <div className="flex gap-2">
              <input
                name="targetQuantity"
                className={inputClass}
                value={targetQuantity}
                onChange={(event) => setTargetQuantity(event.target.value)}
                required
                inputMode="decimal"
                placeholder="12.5"
              />
              <input
                name="targetUom"
                className={`${inputClass} max-w-[6rem]`}
                value={selectedRecipe?.outputUom ?? ''}
                readOnly
                aria-label="Batch size unit of measure"
              />
            </div>
          </Field>

          <Field
            label="Produced at"
            hint="The output lot will sit here"
            tooltip="Where the batch runs. The finished lot is created in this location when the batch is completed."
          >
            <select
              name="batchLocation"
              className={inputClass}
              value={locationId}
              onChange={(event) => setLocationId(event.target.value)}
              required
            >
              <option value="">Select a location…</option>
              {locations.data?.items
                .filter((location) => location.type !== 'scrap')
                .map((location) => (
                  <option key={location.id} value={location.id}>
                    {location.code} — {location.name}
                  </option>
                ))}
            </select>
          </Field>

          <CodeField
            key={`${productItemId ?? 'no-product'}-${locationId || 'no-site'}`}
            label="Batch code"
            value={code}
            onChange={setCode}
            queryKey={['batch-suggest', productItemId, locationId]}
            queryFn={
              productItemId && locationId
                ? () => api.batches.suggestCode(productItemId, locationId)
                : undefined
            }
            required={false}
            tooltip="record type, product, compact date, site, serial and a check character — B-FAPI1-26A8-L1-001-K. Choose the recipe and the line and it fills in. Accept it or type your own; the code is fixed once the batch is created."
            error={
              create.error instanceof ApiError && create.error.status === 409
                ? create.error.message
                : undefined
            }
          />

          <Field label="Scheduled for" hint="Optional">
            <input
              name="scheduledFor"
              className={inputClass}
              type="date"
              value={scheduledFor}
              onChange={(event) => setScheduledFor(event.target.value)}
            />
          </Field>

          <div className="sm:col-span-2 lg:col-span-3">
            <Field label="Notes" hint="Optional">
              <input
                name="batchNotes"
                className={inputClass}
                value={notes}
                onChange={(event) => setNotes(event.target.value)}
              />
            </Field>
          </div>

          <div className="sm:col-span-2 lg:col-span-3">
            {create.error ? (
              <ErrorNote>
                {create.error instanceof ApiError
                  ? create.error.message
                  : 'Could not create the batch.'}
              </ErrorNote>
            ) : null}
            <div className="mt-3 flex gap-2">
              <Button type="submit" disabled={create.isPending}>
                {create.isPending ? 'Creating…' : 'Create batch'}
              </Button>
              <Button tone="ghost" onClick={onClose}>
                Cancel
              </Button>
            </div>
          </div>
        </form>
      </CardBody>
    </Card>
  );
}

/* -------------------------------------------------------------------------- */
/* Detail                                                                      */
/* -------------------------------------------------------------------------- */

function BatchDetail({
  batch,
  onChanged,
  onClose,
}: {
  batch: BatchDto;
  onChanged: () => void;
  onClose: () => void;
}) {
  const [cancelReason, setCancelReason] = useState('');
  const [outputQuantity, setOutputQuantity] = useState('');
  const [showCancel, setShowCancel] = useState(false);
  const [tracing, setTracing] = useState(false);

  const action = useMutation({
    mutationFn: async (kind: 'start' | 'complete' | 'close' | 'cancel') => {
      if (kind === 'start') await api.batches.start(batch.id);
      else if (kind === 'close') await api.batches.close(batch.id, {});
      else if (kind === 'cancel') await api.batches.cancel(batch.id, { reason: cancelReason.trim() });
      else {
        await api.batches.complete(batch.id, {
          ...(outputQuantity.trim() ? { outputQuantity: outputQuantity.trim() } : {}),
        });
      }
    },
    onSuccess: () => {
      onChanged();
      setShowCancel(false);
      setCancelReason('');
    },
  });

  const components = batch.components ?? [];
  const consumptions = batch.consumptions ?? [];

  const componentColumns: DataColumn<BatchComponentBalanceDto>[] = [
    {
      key: 'input',
      header: 'Input',
      primary: true,
      cell: (component) => (
        <>
          <span className="font-mono text-xs">{component.itemCode}</span>
          <br />
          <span className="text-ink-body">{component.itemName}</span>
          {component.isOptional ? (
            <span className="ml-2 text-xs text-ink-muted">(optional)</span>
          ) : null}
        </>
      ),
    },
    {
      key: 'required',
      header: 'Required for this batch',
      cell: (component) => (
        <span className="whitespace-nowrap">
          {formatQuantity(component.requiredQuantity, component.uom)}
        </span>
      ),
    },
    {
      key: 'drawn',
      header: 'Drawn',
      align: 'right',
      cell: (component) => (
        <span className="whitespace-nowrap font-semibold text-ink">
          {formatQuantity(component.consumedQuantity, component.uom)}
        </span>
      ),
    },
    {
      key: 'remaining',
      header: 'Remaining',
      cell: (component) => (
        <span className="whitespace-nowrap">
          {formatQuantity(component.remainingQuantity, component.uom)}
        </span>
      ),
    },
    {
      key: 'range',
      header: 'Allowed range',
      cell: (component) => (
        <span className="whitespace-nowrap text-xs">
          {component.minQuantity && component.maxQuantity
            ? `${component.minQuantity} – ${component.maxQuantity} ${component.uom}`
            : 'exact only'}
        </span>
      ),
    },
    {
      key: 'state',
      header: 'State',
      cell: (component) =>
        component.withinTolerance ? (
          <Badge tone="green">In tolerance</Badge>
        ) : Number(component.remainingQuantity) > 0 ? (
          <Badge tone="hazard">Short by {component.remainingQuantity}</Badge>
        ) : (
          <Badge tone="hazard">Over drawn</Badge>
        ),
    },
  ];

  const consumptionColumns: DataColumn<BatchConsumptionDto>[] = [
    {
      key: 'when',
      header: 'When',
      cell: (consumption) => (
        <span className="whitespace-nowrap text-xs">{formatDateTime(consumption.createdAt)}</span>
      ),
    },
    {
      key: 'item',
      header: 'Item',
      primary: true,
      cell: (consumption) => (
        <span className="font-mono text-xs">{consumption.itemCode}</span>
      ),
    },
    {
      key: 'lot',
      header: 'Lot / location',
      cell: (consumption) => (
        <span className="text-xs">
          {consumption.lotCode} @ {consumption.locationCode}
        </span>
      ),
    },
    {
      key: 'quantity',
      header: 'Quantity',
      align: 'right',
      cell: (consumption) => (
        <span className="whitespace-nowrap font-semibold text-ink">
          {formatQuantity(consumption.quantity, consumption.uom)}
        </span>
      ),
    },
    {
      key: 'value',
      header: 'Value',
      cell: (consumption) => (
        <span className="whitespace-nowrap">{formatQuantity(consumption.value)}</span>
      ),
    },
    {
      key: 'source',
      header: 'Source',
      cell: (consumption) => (
        <Badge tone={consumption.source === 'recipe' ? 'green' : 'accent'}>
          {consumption.source}
        </Badge>
      ),
    },
    {
      key: 'tolerance',
      header: 'Tolerance',
      cell: (consumption) =>
        consumption.isWithinTolerance === null ? (
          '—'
        ) : consumption.isWithinTolerance ? (
          <span className="text-xs text-brand-deep">in tolerance</span>
        ) : (
          <span className="text-xs text-ink-muted">
            off by {consumption.deviationPercent}%
          </span>
        ),
    },
    {
      key: 'reason',
      header: 'Reason',
      cell: (consumption) => (
        <span className="text-xs">{consumption.overrideReason ?? '—'}</span>
      ),
    },
  ];

  return (
    <>
      {tracing ? (
        <TracePanel start={{ kind: 'batch', id: batch.id }} onClose={() => setTracing(false)} />
      ) : null}
      <Card className="mb-6 border-l-4 border-l-brand">
      <CardBody>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-brand-deep">
              {batch.code}
            </p>
            <h2 className="mt-1 text-lg font-bold text-ink">
              {batch.itemCode} — {batch.itemName}
            </h2>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <Badge tone={STATUS_TONES[batch.status]}>{STATUS_LABELS[batch.status]}</Badge>
              <span className="text-sm text-ink-body">
                {batch.recipeCode} v{batch.recipeVersion} · target{' '}
                {formatQuantity(batch.targetQuantity, batch.uom)} · {batch.locationCode}
              </span>
            </div>
            {batch.notes ? <p className="mt-2 text-xs text-ink-muted">{batch.notes}</p> : null}
          </div>

          <Can capability="batch.manage">
            <div className="flex flex-wrap items-end justify-end gap-2">
              {batch.status === 'draft' ? (
                <Button disabled={action.isPending} onClick={() => action.mutate('start')}>
                  Start batch
                </Button>
              ) : null}
              {batch.status === 'in_progress' ? (
                <>
                  <div className="w-full sm:w-auto">
                    <div className="mb-1 flex items-center gap-1.5">
                      <label htmlFor="outputQuantity" className="text-sm font-bold text-ink">
                        Produced quantity ({batch.uom})
                      </label>
                      <Tooltip
                        subject="Produced quantity"
                        text="How much actually came out of this batch, in the item's base unit. It becomes the output movement and the finished lot, priced from everything the batch drew. It defaults to the target, so adjust it only when the run produced more or less than planned."
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        id="outputQuantity"
                        name="outputQuantity"
                        className={`${inputClass} max-w-[9rem]`}
                        value={outputQuantity}
                        onChange={(event) => setOutputQuantity(event.target.value)}
                        placeholder={batch.targetQuantity}
                        inputMode="decimal"
                      />
                      <span className="whitespace-nowrap text-xs text-ink-muted">
                        target {formatQuantity(batch.targetQuantity, batch.uom)}
                      </span>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button disabled={action.isPending} onClick={() => action.mutate('complete')}>
                      {action.isPending ? 'Working…' : 'Complete'}
                    </Button>
                    <Button tone="danger" onClick={() => setShowCancel(true)}>
                      Cancel batch
                    </Button>
                  </div>
                </>
              ) : null}
              {batch.status === 'completed' ? (
                <Button disabled={action.isPending} onClick={() => action.mutate('close')}>
                  Close batch
                </Button>
              ) : null}
              {batch.outputLotId ? (
                <Button tone="ghost" onClick={() => setTracing((current) => !current)}>
                  {tracing ? 'Hide trace' : 'Trace output'}
                </Button>
              ) : null}
              <Button tone="ghost" onClick={onClose}>
                Close
              </Button>
            </div>
          </Can>
        </div>

        {showCancel ? (
          <div className="mt-4 max-w-xl">
            <Field
              label="Why cancel this batch?"
              hint="Anything already drawn is returned to stock automatically"
            >
              <input
                name="cancelReason"
                className={inputClass}
                value={cancelReason}
                onChange={(event) => setCancelReason(event.target.value)}
                placeholder="e.g. wrong lot was issued"
              />
            </Field>
            <div className="mt-3 flex gap-2">
              <Button
                tone="danger"
                disabled={cancelReason.trim().length < 3 || action.isPending}
                onClick={() => action.mutate('cancel')}
              >
                Confirm cancellation
              </Button>
              <Button tone="ghost" onClick={() => setShowCancel(false)}>
                Keep the batch
              </Button>
            </div>
          </div>
        ) : null}

        {action.error ? (
          <div className="mt-4">
            <ErrorNote>
              {action.error instanceof ApiError ? action.error.message : 'That action failed.'}
            </ErrorNote>
          </div>
        ) : null}

        <dl className="mt-4 grid gap-4 text-sm sm:grid-cols-2 lg:grid-cols-4">
          <Metric label="Started" value={formatDateTime(batch.startedAt)} />
          <Metric label="Completed" value={formatDateTime(batch.completedAt)} />
          <Metric label="Drawn from stock" value={formatQuantity(batch.consumedValue)} />
          <Metric
            label="Produced"
            value={
              batch.outputQuantity
                ? `${formatQuantity(batch.outputQuantity, batch.uom)}${
                    batch.yieldPercent ? ` · ${batch.yieldPercent}% yield` : ''
                  }`
                : '—'
            }
          />
          <Metric
            label="Output lot"
            value={batch.outputLotCode ?? '—'}
          />
          <Metric
            label="Output unit cost"
            value={batch.outputUnitCost ? formatUnitCost(batch.outputUnitCost, batch.uom) : '—'}
          />
        </dl>

        <div className="mt-2 flex items-center gap-1.5">
          <h3 className="text-sm font-bold uppercase tracking-wide text-ink-muted">
            Mass balance
          </h3>
          <Tooltip
            subject="Mass balance"
            text="The recipe scaled to this batch's size, against what has actually been drawn. “Short by” just means the batch is not finished yet — material is normally added in stages. “Over drawn” means more was taken than the tolerance allows, which is why it needed a reason."
          />
        </div>
        <div className="mt-2">
          <DataTable
            rows={components}
            columns={componentColumns}
            rowKey={(component) => component.componentId}
          />
        </div>

        {batch.status === 'in_progress' ? (
          <Can capability="batch.manage">
            <ConsumptionForm batch={batch} onRecorded={onChanged} />
          </Can>
        ) : null}

        <h3 className="mt-6 text-sm font-bold uppercase tracking-wide text-ink-muted">
          Consumption log ({consumptions.length})
        </h3>
        <div className="mt-2">
          <DataTable
            rows={consumptions}
            columns={consumptionColumns}
            rowKey={(consumption) => consumption.id}
            empty="Nothing drawn yet."
          />
        </div>
      </CardBody>
      </Card>
    </>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs font-bold uppercase tracking-wide text-ink-muted">{label}</dt>
      <dd className="text-ink-body">{value}</dd>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Recording a draw                                                            */
/* -------------------------------------------------------------------------- */

function ConsumptionForm({ batch, onRecorded }: { batch: BatchDto; onRecorded: () => void }) {
  const components = batch.components ?? [];
  const [componentId, setComponentId] = useState(components[0]?.componentId ?? '');
  const [adHocItemId, setAdHocItemId] = useState('');
  const [balanceKey, setBalanceKey] = useState('');
  const [quantity, setQuantity] = useState('');
  const [uom, setUom] = useState(components[0]?.uom ?? '');
  const [reason, setReason] = useState('');

  const component = components.find((candidate) => candidate.componentId === componentId) ?? null;
  const isAdHoc = componentId === '';
  const itemId = isAdHoc ? adHocItemId : (component?.itemId ?? '');

  const items = useQuery({
    queryKey: ['items', { limit: 200 }],
    queryFn: () => api.items.list({ limit: 200 }),
    enabled: isAdHoc,
  });

  // Stock actually available to draw: one row per lot and location.
  const balances = useQuery({
    queryKey: ['inventory', 'balances', { itemId }],
    queryFn: () => api.inventory.balances({ itemId, hideEmpty: true, limit: 100 }),
    enabled: Boolean(itemId),
  });

  const record = useMutation({
    mutationFn: async () => {
      const [lotId, locationId] = balanceKey.split(':');
      await api.batches.consume(batch.id, {
        ...(isAdHoc ? {} : { componentId }),
        itemId,
        lotId,
        locationId,
        quantity: quantity.trim(),
        uom,
        isSubstitute: false,
        ...(reason.trim() ? { reason: reason.trim() } : {}),
      });
    },
    onSuccess: () => {
      setQuantity('');
      setReason('');
      onRecorded();
    },
  });

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    record.mutate();
  }

  return (
    <div className="mt-6 rounded-md border border-surface-muted bg-canvas p-4">
      <h3 className="text-sm font-bold uppercase tracking-wide text-ink-muted">Record a draw</h3>
      <p className="mt-1 text-sm font-light text-ink-body">
        This moves the stock immediately. Drawing a little less than planned is normal; drawing
        past the allowed range needs a reason.
      </p>

      <form
        autoComplete="off"
        onSubmit={handleSubmit}
        className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4"
      >
        <Field
          label="Recipe line"
          tooltip="Which line of the recipe this draw is for. Pick “Ad-hoc” for material the recipe never called for, such as a wash or a trial addition — it is allowed, but it always needs a reason."
        >
          <select
            name="consumptionComponent"
            className={inputClass}
            value={componentId}
            onChange={(event) => {
              setComponentId(event.target.value);
              setBalanceKey('');
              const next = components.find((c) => c.componentId === event.target.value);
              setUom(next?.uom ?? '');
            }}
          >
            <option value="">Ad-hoc draw (not on the recipe)</option>
            {components.map((candidate) => (
              <option key={candidate.componentId} value={candidate.componentId}>
                {candidate.itemCode} — needs {candidate.requiredQuantity} {candidate.uom} (drawn{' '}
                {candidate.consumedQuantity})
              </option>
            ))}
          </select>
        </Field>

        {isAdHoc ? (
          <Field label="Item">
            <select
              name="adHocItem"
              className={inputClass}
              value={adHocItemId}
              onChange={(event) => {
                setAdHocItemId(event.target.value);
                setBalanceKey('');
                const item = items.data?.items.find((i: ItemDto) => i.id === event.target.value);
                setUom(item?.baseUom ?? '');
              }}
              required
            >
              <option value="">Select an item…</option>
              {items.data?.items.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.code} — {item.name}
                </option>
              ))}
            </select>
          </Field>
        ) : null}

        <Field
          label="Draw from"
          hint="Lot and location with stock"
          tooltip="Which lot the material is taken out of. Only lots marked available are offered — anything on quality hold has to be released first, so it will not appear here."
        >
          <select
            name="consumptionBalance"
            className={inputClass}
            value={balanceKey}
            onChange={(event) => setBalanceKey(event.target.value)}
            required
            disabled={!itemId}
          >
            <option value="">Select stock…</option>
            {(balances.data?.items ?? []).map((balance) => (
              <option
                key={`${balance.lotId}:${balance.locationId}`}
                value={`${balance.lotId}:${balance.locationId}`}
              >
                {balance.lotCode} @ {balance.locationCode} — {balance.quantity} {balance.baseUom}
                {balance.status === 'quarantine' ? ' (quarantined)' : ''}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Quantity">
          <div className="flex gap-2">
            <input
              name="consumptionQuantity"
              className={inputClass}
              value={quantity}
              onChange={(event) => setQuantity(event.target.value)}
              required
              inputMode="decimal"
              placeholder={component?.remainingQuantity ?? '0'}
            />
            <input
              name="consumptionUom"
              className={`${inputClass} max-w-[5rem]`}
              value={uom}
              onChange={(event) => setUom(event.target.value)}
              required
              aria-label="Unit of measure"
            />
          </div>
        </Field>

        <div className="sm:col-span-2 lg:col-span-4">
          <Field label="Reason" hint="Needed for an ad-hoc draw, a substitute, or drawing past the range">
            <input
              name="consumptionReason"
              className={inputClass}
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder="Optional unless an override is needed"
            />
          </Field>
        </div>

        <div className="sm:col-span-2 lg:col-span-4">
          {record.error ? (
            <ErrorNote>
              {record.error instanceof ApiError ? record.error.message : 'Could not record the draw.'}
            </ErrorNote>
          ) : null}
          <div className="mt-3">
            <Button type="submit" disabled={record.isPending || !balanceKey}>
              {record.isPending ? 'Drawing…' : 'Record draw'}
            </Button>
          </div>
        </div>
      </form>

      {!record.error ? (
        <div className="mt-3">
          {/* <InfoNote>
            The draw posts a <span className="font-mono text-xs">consume</span> movement straight
            away, so inventory and the batch never disagree.
          </InfoNote> */}
        </div>
      ) : null}
    </div>
  );
}
